<script lang="ts">
  import { client, trpcTryCatch } from "@/helpers/trpc/trpc-client";
  import Swal from "sweetalert2";
  import i18n from "@/stores/i18n";

  import { Button } from "@/components/ui/button";
  import * as Dialog from "@/components/ui/dialog";
  import { Image } from "@/components/ui/image";
  import { Input } from "@/components/ui/input";
  import { Typography } from "@/components/ui/typography/";

  import CloseIcon from "@/images/Close.svg";
  import GoogleIcon from "@/images/google.svg";
  import Background from "@/images/home_bg.png?raw";
  import DiscordIcon from "@/images/logos_discord-icon.svg";
  import EmailIcon from "@/images/mail.svg";
  import MicrosoftIcon from "@/images/ms-icon.svg";
  import PlayPlex from "@/images/PlayPlex.svg";

  const t = (key: string, options?: any): string | object =>
    $i18n.t(key, { ns: "common", ...options });

  interface Props {
    open?: boolean;
    onOpenChange?: () => void;
    email?: string;
  }

  let {
    open = $bindable(false),
    onOpenChange = () => {},
    email = $bindable("")
  }: Props = $props();

  const handleEmailLogin = (e: Event) => {
    e.preventDefault();
    trpcTryCatch(async () => {
      await client.auth.loginEmail.query(email);
      Swal.fire({
        title: t("login.email_success.title") as string,
        text: t("login.email_success.message") as string,
        icon: "success",
        confirmButtonText: t("login.email_success.button") as string
      });
    }).then(() => {
      email = "";
    });
  };

  const loginSocial = async (provider: "google" | "discord" | "microsoft") => {
    Swal.showLoading();
    const { url } = await client.auth.loginSocial.query(provider);
    window.location.href = url;
  };
</script>

<Dialog.Root {open} {onOpenChange}>
  <Dialog.Content
    class="!w-fit !max-w-[90vw] overflow-hidden rounded-2xl border border-dark-gray bg-theme-black p-0">
    <div class="flex">
      <div class="w-[558px] max-w-[90vw] p-10">
        <Dialog.Header>
          <div class="flex items-center justify-between">
            <Dialog.Title class="font-poppins  font-[600]">
              <Typography variant="h2">{t("login.title")}</Typography>
            </Dialog.Title>

            <Button
              onclick={() => {
                open = false;
              }}
              variant="secondary"
              class="p-[10px]"
              ><Image
                src={CloseIcon}
                alt={t("login.close_button") as string} /></Button>
          </div>
          <Dialog.Description class="mt-3">
            <Typography variant="large" color="muted" class="text-left">
              {t("login.no_account_message")}
            </Typography>
          </Dialog.Description>
        </Dialog.Header>

        <form onsubmit={handleEmailLogin} class="mt-8">
          <Typography as="large" color="light_gray"
            >{t("login.email_label")}</Typography>
          <div class="mt-2 rounded-xl border border-dark-gray">
            <Image
              src={EmailIcon}
              class="ml-[16px]  inline-block h-6 w-6"
              alt={t("login.email_icon") as string} />
            <Input
              bind:value={email}
              class="-ml-1 inline-block w-[80%] border-none px-[12px] py-6 font-poppins text-[16px] font-[500] leading-[150%] placeholder:!font-normal placeholder:text-medium-gray focus-visible:ring-0 focus-visible:ring-offset-0"
              placeholder={t("login.email_placeholder") as string}>
            </Input>
          </div>
          <Button
            type="submit"
            variant="accent"
            typographyVariant="large"
            class="mt-[30px] w-full rounded-xl py-4 !font-normal "
            >{t("login.email_button")}</Button>
        </form>

        <div class="my-8 flex w-full justify-center">
          <Typography color="muted">{t("login.or_divider")}</Typography>
        </div>

        <div class="mt-[32px] flex justify-center gap-3">
          <Button
            onclick={() => loginSocial("google")}
            typographyVariant="large"
            typographyWeight="semibold"
            variant="secondary"
            class="lg:!px-16">
            <Image
              src={GoogleIcon}
              alt={t("login.google_icon") as string}
              class="mx-[12px]  ml-[16px] inline-block h-6 w-6" />
          </Button>

          <Button
            onclick={() => loginSocial("discord")}
            typographyVariant="large"
            typographyWeight="semibold"
            variant="secondary"
            class="lg:!px-16">
            <Image
              src={DiscordIcon}
              alt={t("login.discord_icon") as string}
              class="mx-[12px]  ml-[16px] inline-block h-6 w-6 " />
          </Button>

          <Button
            onclick={() => loginSocial("microsoft")}
            variant="secondary"
            class="lg:!px-16">
            <Image
              src={MicrosoftIcon}
              alt={t("login.microsoft_icon") as string}
              class="mx-[12px]  ml-[16px] inline-block h-6 w-6" />
          </Button>
        </div>
      </div>
      <div class="relative hidden h-full !w-[438px] lg:block">
        <div
          class=" absolute inset-0 z-10 -ml-2 border-none"
          style="background: linear-gradient(270deg, rgba(0, 0, 0, 0.00) 0%, #121212 100%, #191919 100.01%), linear-gradient(0deg, rgba(155, 48, 255, 0.15) 0%, rgba(155, 48, 255, 0.15) 100%);">
        </div>
        <Image
          class="object-cover object-center"
          src={Background}
          alt={t("login.background_image") as string} />
        <div
          class="absolute left-0 top-0 z-50 flex h-full w-full items-center justify-center">
          <Image src={PlayPlex} class="w-48" alt={t("login.logo") as string} />
        </div>
      </div>
    </div>
  </Dialog.Content>
</Dialog.Root>
